import { useEffect, useState } from 'react';
import { api, money } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';
import { formatQuantity } from '../lib/quantity.js';

/**
 * What is on hand, where — and what it is worth, which is a different question.
 *
 * ⚠ QUANTITY IS PER LOCATION AND VALUE IS PER ITEM, AND THE TWO DO NOT LINE UP.
 *   Cost lives on the cost layer and a layer has no location, so there is no
 *   such figure as "what this shelf is worth" for the report to give and none
 *   is invented here. `item_value_cents` is the item's WHOLE value repeated on
 *   each of its location rows, which is why it is rendered once per item, on
 *   the first of its rows, under a column that says so on its face.
 *
 *   The same trap sits in the totals. `totals.entityWideValue` is named that
 *   for a reason: the payload echoes `locationId` a line above it, so a total
 *   called `value` sitting under a location filter would read as "what this
 *   location holds" — and it is not that figure and never can be. Rendering it
 *   as a location's value would state, on the fixture the API's own tests use,
 *   that an overflow store holds R86.17 when it holds R33.33.
 *
 *   THE REPORT THAT ANSWERS "WHAT IS THIS LOCATION'S STOCK WORTH" IS THE STOCK
 *   VALUATION WITH THE SAME locationId — a plain sum over the movements that
 *   happened there. So this screen asks it, every time, and shows both figures
 *   with their names on them rather than apportioning one into the other. An
 *   apportionment is exactly what the API refused to do, and it refused for the
 *   good reason that any split of a FIFO layer across shelves is a guess.
 */
export function StockOnHand({ entity }) {
  const today = new Date().toISOString().slice(0, 10);
  const [asAt, setAsAt] = useState(today);
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [onHand, setOnHand] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!entity?.id) return;
    api.get(`/api/entities/${entity.id}/stock-locations?active=true`)
      .then((d) => setLocations(d.locations)).catch(() => {});
  }, [entity?.id]);

  const run = async (at = asAt, loc = locationId) => {
    setBusy(true); setErr(null);
    const q = `asAt=${at}${loc ? `&locationId=${loc}` : ''}`;
    try {
      // BOTH, and with the SAME filter. The valuation is the only report that
      // can answer what one location is worth; the on-hand is the only one that
      // can say where things are. Neither alone is the screen.
      const [oh, val] = await Promise.all([
        api.get(`/api/reports/${entity.id}/stock-on-hand?${q}`),
        api.get(`/api/reports/${entity.id}/stock-valuation?${q}`),
      ]);
      setOnHand(oh); setValuation(val);
    } catch (e) {
      setErr(e.message); setOnHand(null); setValuation(null);
    } finally { setBusy(false); }
  };

  useEffect(() => { if (entity?.id) run(); /* eslint-disable-next-line */ }, [entity?.id]);

  // One entry per item, in row order, so the value cell can span the item's
  // locations instead of being repeated once per shelf as the payload does.
  const groups = [];
  const seen = new Map();
  for (const row of onHand?.rows ?? []) {
    let group = seen.get(row.item_id);
    if (!group) {
      group = { item: row, rows: [] };
      seen.set(row.item_id, group);
      groups.push(group);
    }
    group.rows.push(row);
  }

  const filtered = Boolean(onHand?.locationId);
  const location = locations.find((l) => l.id === onHand?.locationId);
  const agrees = onHand && valuation
    && onHand.totals.entityWideValueCents === valuation.totals.valueCents;

  return (
    <>
      <ReportHead entity={entity} title="Stock on hand"
                  sub="Quantity by location, value by item — because cost lives on the layer and a layer has no shelf." />

      <div className="controls">
        <label className="field">
          <span>As at</span>
          <input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)} />
        </label>
        <label className="field" style={{ minWidth: 220 }}>
          <span>Location</span>
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">Every location</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
          </select>
        </label>
        <button className="btn" onClick={() => run()} disabled={busy}>
          {busy ? 'Running' : 'Run'}
        </button>
      </div>

      {err && <div className="error">{err}</div>}

      {onHand && (onHand.rows.length === 0 ? (
        <Empty>
          <p>Nothing on hand at {onHand.asAt}{filtered && location ? ` in ${location.name}` : ''}.</p>
          <p>Take stock on, or capture a purchase, to see it here.</p>
        </Empty>
      ) : (
        <>
          <div className="scroll-x">
            <table className="ledger">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Code</th>
                  <th>Item</th>
                  <th style={{ width: 220 }}>Location</th>
                  <th className="num" style={{ width: 130 }}>Quantity here</th>
                  <th className="num" style={{ width: 140 }}>Quantity everywhere</th>
                  {/* The column name carries the caveat, because a reader who
                      has to consult a note to interpret a figure will not. */}
                  <th className="num" style={{ width: 150 }}>Value, all locations</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => g.rows.map((row, i) => (
                  <tr key={`${row.item_id}-${row.location_id}`}>
                    {i === 0 && (
                      <>
                        <td className="code figure" rowSpan={g.rows.length}>{row.item_code}</td>
                        <td rowSpan={g.rows.length}>{row.item_name}</td>
                      </>
                    )}
                    <td>
                      <span className="code figure">{row.location_code}</span> {row.location_name}
                    </td>
                    <td className="num figure">
                      {formatQuantity(row.quantity)} {row.unit ?? ''}
                    </td>
                    {i === 0 && (
                      <>
                        <td className="num figure" rowSpan={g.rows.length}>
                          {formatQuantity(row.item_quantity)} {row.unit ?? ''}
                        </td>
                        {/* ONCE PER ITEM. The payload repeats it on every
                            location row; printing it there too would invite
                            the reader to add the column up and double a
                            two-location item. */}
                        <td className="num" rowSpan={g.rows.length}>
                          <Money value={row.item_value} />
                        </td>
                      </>
                    )}
                  </tr>
                )))}
              </tbody>
            </table>
          </div>

          <div className="stat-row">
            <div className="stat">
              <div className="stat__label">Items</div>
              <div className="stat__value">{onHand.totals.items}</div>
            </div>
            <div className="stat">
              <div className="stat__label">Locations</div>
              <div className="stat__value">{onHand.totals.locations}</div>
            </div>
            <div className="stat">
              <div className="stat__label">
                {filtered ? 'Value of these items, entity-wide' : 'Value of all stock'}
              </div>
              <div className="stat__value">{money(onHand.totals.entityWideValue)}</div>
            </div>
            {filtered && valuation && (
              <div className="stat">
                <div className="stat__label">
                  What {location ? location.name : 'this location'} holds
                </div>
                <div className="stat__value stat__value--credit">
                  {money(valuation.totals.value)}
                </div>
              </div>
            )}
          </div>

          {filtered ? (
            <div className="note note--warn">
              <strong>The two totals above answer different questions.</strong>{' '}
              {onHand.note}{' '}
              The second figure is the stock valuation for this same location and date — a plain
              sum over the movements that happened there — and it is the one to use when the
              question is what this room is worth.
            </div>
          ) : (
            <div className="note">
              {agrees ? (
                <>
                  <strong>{money(onHand.totals.entityWideValue)}</strong> — the same figure the
                  stock valuation reports at {onHand.asAt}, and the figure that ties to the
                  inventory control account.
                </>
              ) : (
                <>
                  <strong>These two reports disagree.</strong> Stock on hand totals{' '}
                  {money(onHand.totals.entityWideValue)} and the stock valuation totals{' '}
                  {money(valuation?.totals?.value)} at the same date, over the same movements.
                  They are computed from one ledger and should never differ — do not rely on
                  either figure until this is explained.
                </>
              )}{' '}
              {onHand.note}
            </div>
          )}
        </>
      ))}
    </>
  );
}
